import React from "react";
import { renderTextWithPlaceholders as rT } from "./PlaceholderToken";

export default function ResumePreviewATS({ data, editable = false, onPlaceholderFill }) {
  const c = data?.contact || {};
  const onFill = editable ? onPlaceholderFill : undefined;

  const contactFields = [
    { val: c.email,    path: "contact.email"    },
    { val: c.phone,    path: "contact.phone"    },
    { val: c.location, path: "contact.location" },
    { val: c.linkedin, path: "contact.linkedin" },
  ].filter(x => x.val);

  return (
    <div className="bg-white font-sans text-[#111] text-[13px] leading-relaxed p-8 max-w-[750px] mx-auto shadow-sm">
      {c.name && (
        <h1 className="text-[22px] font-bold text-center tracking-wide mb-0.5">
          {rT(c.name, "contact.name", editable, onFill)}
        </h1>
      )}
      {contactFields.length > 0 && (
        <p className="text-center text-[11px] text-[#444] mb-5">
          {contactFields.map((x, i) => (
            <React.Fragment key={x.path}>
              {i > 0 && "  |  "}
              {rT(x.val, x.path, editable, onFill)}
            </React.Fragment>
          ))}
        </p>
      )}

      {data?.summary && (
        <Section title="Professional Summary">
          <p>{rT(data.summary, "summary", editable, onFill)}</p>
        </Section>
      )}

      {data?.experience?.length > 0 && (
        <Section title="Work Experience">
          {data.experience.map((exp, i) => (
            <div key={i} className="mb-4">
              <div className="flex justify-between items-baseline">
                <span className="font-bold">
                  {rT(exp.title, `experience.${i}.title`, editable, onFill)}
                  {" — "}
                  {rT(exp.company, `experience.${i}.company`, editable, onFill)}
                </span>
                <span className="text-[11px] text-[#666]">
                  {rT(exp.start_date, `experience.${i}.start_date`, editable, onFill)}
                  {" – "}
                  {rT(exp.end_date, `experience.${i}.end_date`, editable, onFill)}
                </span>
              </div>
              {exp.location && (
                <div className="text-[11px] text-[#666] mb-1">
                  {rT(exp.location, `experience.${i}.location`, editable, onFill)}
                </div>
              )}
              <ul className="list-disc list-outside ml-4 space-y-0.5">
                {(exp.bullets || []).map((b, j) => (
                  <li key={j}>{rT(b, `experience.${i}.bullets.${j}`, editable, onFill)}</li>
                ))}
              </ul>
            </div>
          ))}
        </Section>
      )}

      {data?.education?.length > 0 && (
        <Section title="Education">
          {data.education.map((edu, i) => (
            <div key={i} className="mb-2">
              <div className="flex justify-between items-baseline">
                <span className="font-bold">
                  {rT(edu.degree, `education.${i}.degree`, editable, onFill)}
                </span>
                <span className="text-[11px] text-[#666]">
                  {rT(edu.graduation_date, `education.${i}.graduation_date`, editable, onFill)}
                </span>
              </div>
              <div className="text-[11px] text-[#666]">
                {rT(edu.institution, `education.${i}.institution`, editable, onFill)}
                {edu.location && <>{" · "}{rT(edu.location, `education.${i}.location`, editable, onFill)}</>}
              </div>
              {(edu.details || []).map((d, j) => (
                <div key={j} className="text-[11px]">
                  {rT(d, `education.${i}.details.${j}`, editable, onFill)}
                </div>
              ))}
            </div>
          ))}
        </Section>
      )}

      {data?.skills?.length > 0 && (
        <Section title="Skills">
          {data.skills.map((sk, i) => (
            <div key={i} className="mb-1">
              <span className="font-bold">
                {rT(sk.name, `skills.${i}.name`, editable, onFill)}:{" "}
              </span>
              <span className="text-[#333]">
                {(sk.items || []).map((item, j) => (
                  <React.Fragment key={j}>
                    {j > 0 && ", "}
                    {rT(item, `skills.${i}.items.${j}`, editable, onFill)}
                  </React.Fragment>
                ))}
              </span>
            </div>
          ))}
        </Section>
      )}

      {data?.certifications?.length > 0 && (
        <Section title="Certifications">
          {data.certifications.map((cert, i) => (
            <div key={i} className="mb-0.5">
              <span className="font-bold">
                {rT(cert.name, `certifications.${i}.name`, editable, onFill)}
              </span>
              {cert.issuer && (
                <span className="text-[#555]">
                  {" · "}{rT(cert.issuer, `certifications.${i}.issuer`, editable, onFill)}
                </span>
              )}
              {cert.date && (
                <span className="text-[#555]">
                  {" · "}{rT(cert.date, `certifications.${i}.date`, editable, onFill)}
                </span>
              )}
            </div>
          ))}
        </Section>
      )}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="mb-4">
      <div className="font-bold uppercase text-[11px] tracking-widest border-b border-[#111] pb-0.5 mb-2">
        {title}
      </div>
      {children}
    </div>
  );
}
